import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Button, Badge } from '../../../components/ui';
import { ClipboardList, CheckCircle2, XCircle, AlertTriangle, Info, Trash2, Plus } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { useAuth } from '../../../app/auth';

// ════════════════════════════════════════════════════════════════
// LISTAS MANUALES — FASE 1: SOLO VALIDACIÓN (dry-run).
// Modo rápido (inputs tipo POS) + modo pegar lista (textarea).
// La tabla temporal vive SOLO en el frontend: eliminar/limpiar no toca
// backend. "Validar lista" llama POST /api/listas-manuales/validar, que
// NO guarda, NO crea tickets, NO afecta saldos ni escribe auditoría.
// El botón "Guardar" llegará en Fase 2.
// ════════════════════════════════════════════════════════════════

interface SorteoItem { id: string; nombre: string; tipo_id: string; estado: string; }
interface LocalItem { id: string; nombre: string; }
interface JugadaParseada { num1: string; num2: string | null; monto: string; }
interface LineaResultado {
  orden: number;
  linea_original: string;
  valida: boolean;
  jugadas: JugadaParseada[];
  errores: string[];
  advertencias: string[];
}
interface ResultadoValidacion {
  valido: boolean;
  fecha_operativa: string;
  total_lineas: number;
  lineas_validas: number;
  total_monto: string;
  errores_globales: string[];
  advertencias_globales: string[];
  lineas: LineaResultado[];
}

interface LineaTemp {
  linea: string;       // texto exacto que se manda al parser
  tipo: string;
  monto: number;       // total de la línea (solo display)
}

const TIPOS = ['NORMAL', 'REVENTADO', 'MONAZOS', 'FECHAS', 'PARLEY'] as const;
type TipoLista = typeof TIPOS[number];
type MonazoTipo = 'ORDEN' | 'DESORDEN' | 'P2' | 'U2';

const FORMATOS: { value: string; label: string }[] = [
  { value: 'NUMERO_MONTO', label: 'Número / Monto' },
  { value: 'MONTO_NUMERO', label: 'Monto / Número' },
  { value: 'AUTO', label: 'Auto-detectar (pegar lista)' },
];

const AYUDA: Record<TipoLista, { ejemplos: string[]; nota: string }> = {
  NORMAL: {
    ejemplos: ['07 500', '23 1000', '500 07  (monto/número)'],
    nota: 'Un número de 2 dígitos (00-99) y un monto por línea.',
  },
  REVENTADO: {
    ejemplos: ['07 500 200   (directo 500 + reventado 200)', '23 1000      (solo directo)'],
    nota: 'Número, monto directo y reventado opcional. Genera REV1/REV2 como el POS.',
  },
  MONAZOS: {
    ejemplos: ['970 500 ORDEN', '970 500 DESORDEN', '97 500 P2', '70 500 U2'],
    nota: 'Tres dígitos para ORDEN/DESORDEN; P2/U2 con 2 dígitos (o derivan de 3 como el POS).',
  },
  FECHAS: {
    ejemplos: ['12-06 500', '12/06 500', '1206 500 (con formato Número/Monto)'],
    nota: 'Día 1-31 y mes 1-12. Se normaliza siempre a dd-mm.',
  },
  PARLEY: {
    ejemplos: ['07-23 500', '07 23 500', '500 07 23 (monto/número)'],
    nota: 'Dos números 00-99 distintos por línea.',
  },
};

const pad2 = (v: string) => v.padStart(2, '0');
const soloDigitos = (v: string) => v.replace(/[^0-9]/g, '');

// Input numérico grande (cómodo en celular)
function NumInput(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onEnter: () => void;
  maxLen: number;
  placeholder?: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  ancho?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-1">{props.label}</label>
      <input
        ref={props.inputRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        spellCheck={false}
        maxLength={props.maxLen}
        value={props.value}
        placeholder={props.placeholder}
        onChange={(e) => props.onChange(soloDigitos(e.target.value).slice(0, props.maxLen))}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); props.onEnter(); } }}
        className={`${props.ancho || 'w-full'} px-4 py-3 text-xl text-center font-bold border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:outline-none`}
      />
    </div>
  );
}

export function ListasManualesPage() {
  const { user } = useAuth();
  const esMiniBanca = user?.role === 'MINI_BANCA';
  const miLocalId = (user as any)?.localId as string | undefined;

  const [sorteos, setSorteos] = useState<SorteoItem[]>([]);
  const [locales, setLocales] = useState<LocalItem[]>([]);
  const [sorteoId, setSorteoId] = useState('');
  const [localId, setLocalId] = useState('');
  const [tipo, setTipo] = useState<TipoLista>('NORMAL');
  const [formato, setFormato] = useState('NUMERO_MONTO');
  const [modo, setModo] = useState<'RAPIDO' | 'PEGAR'>('RAPIDO');
  const [texto, setTexto] = useState('');
  const [lineasTemp, setLineasTemp] = useState<LineaTemp[]>([]);
  const [errorEntrada, setErrorEntrada] = useState<string | null>(null);
  const [validando, setValidando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoValidacion | null>(null);
  const [errorHttp, setErrorHttp] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  // FASE 2: confirmación (ventas reales)
  const [modalConfirmar, setModalConfirmar] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [exito, setExito] = useState<{
    ticket_id: string; ticket_number: string | null; lineas: number;
    jugadas: number; total: string; advertencias: string[];
  } | null>(null);
  // El texto exacto que se validó (la confirmación usa ESTE, no el estado
  // actual: si el usuario edita después de validar, debe re-validar)
  const [textoValidado, setTextoValidado] = useState<string | null>(null);
  const [formatoValidado, setFormatoValidado] = useState<string>('NUMERO_MONTO');

  // Campos del modo rápido
  const [campo1, setCampo1] = useState('');  // número (o monto si MONTO_NUMERO)
  const [campo2, setCampo2] = useState('');  // monto (o número)
  const [campo3, setCampo3] = useState('');  // reventado / monto fechas-parley
  const [dia, setDia] = useState('');
  const [mes, setMes] = useState('');
  const [tipoMonazo, setTipoMonazo] = useState<MonazoTipo>('ORDEN');

  const ref1 = useRef<HTMLInputElement>(null);
  const ref2 = useRef<HTMLInputElement>(null);
  const ref3 = useRef<HTMLInputElement>(null);
  const refDia = useRef<HTMLInputElement>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [ss, ls] = await Promise.all([
        fetchAPI<SorteoItem[]>(`/sorteos?estado=ABIERTO`),
        fetchAPI<LocalItem[]>('/reportes/locales?solo_operativos=true'),
      ]);
      setSorteos(ss);
      setLocales(esMiniBanca ? ls.filter((l) => l.id !== miLocalId) : ls);
    } catch (e: any) {
      setErrorHttp(e.message || 'Error al cargar sorteos/vendedores');
    } finally {
      setCargando(false);
    }
  }, [esMiniBanca, miLocalId]);

  useEffect(() => { cargar(); }, [cargar]);

  const sorteosDelTipo = useMemo(() => sorteos.filter((s) => s.tipo_id === tipo), [sorteos, tipo]);

  useEffect(() => {
    if (!sorteosDelTipo.find((s) => s.id === sorteoId)) {
      setSorteoId(sorteosDelTipo[0]?.id || '');
    }
  }, [sorteosDelTipo, sorteoId]);

  const limpiarCampos = () => {
    setCampo1(''); setCampo2(''); setCampo3('');
    setDia(''); setMes('');
    setErrorEntrada(null);
    setTimeout(() => (tipo === 'FECHAS' ? refDia : ref1).current?.focus(), 30);
  };

  // Cambiar tipo limpia entrada temporal (las líneas son por tipo)
  const cambiarTipo = (t: TipoLista) => {
    setTipo(t);
    setLineasTemp([]);
    setResultado(null);
    setCampo1(''); setCampo2(''); setCampo3(''); setDia(''); setMes('');
    setErrorEntrada(null);
  };

  const numeroEnPrimero = formato !== 'MONTO_NUMERO';

  // ── Agregar línea desde el modo rápido ──
  const agregarLinea = () => {
    setErrorEntrada(null);
    let linea = '';
    let montoLinea = 0;

    if (tipo === 'NORMAL') {
      const numero = numeroEnPrimero ? campo1 : campo2;
      const monto = numeroEnPrimero ? campo2 : campo1;
      if (!numero || !monto || Number(monto) <= 0) { setErrorEntrada('Digite número y monto'); return; }
      linea = `${pad2(numero)} ${Number(monto)}`;
      montoLinea = Number(monto);
    } else if (tipo === 'REVENTADO') {
      if (!campo1 || !campo2 || Number(campo2) <= 0) { setErrorEntrada('Digite número y monto directo'); return; }
      const rev = campo3 && Number(campo3) > 0 ? ` ${Number(campo3)}` : '';
      linea = `${pad2(campo1)} ${Number(campo2)}${rev}`;
      montoLinea = Number(campo2) + (Number(campo3) || 0);
    } else if (tipo === 'FECHAS') {
      if (!dia || !mes || !campo2 || Number(campo2) <= 0) { setErrorEntrada('Digite día, mes y monto'); return; }
      const d = Number(dia), m = Number(mes);
      if (d < 1 || d > 31) { setErrorEntrada('Día fuera de rango (1-31)'); return; }
      if (m < 1 || m > 12) { setErrorEntrada('Mes fuera de rango (1-12)'); return; }
      linea = `${pad2(dia)}-${pad2(mes)} ${Number(campo2)}`;
      montoLinea = Number(campo2);
    } else if (tipo === 'PARLEY') {
      if (!campo1 || !campo2 || !campo3 || Number(campo3) <= 0) { setErrorEntrada('Digite los dos números y el monto'); return; }
      if (pad2(campo1) === pad2(campo2)) { setErrorEntrada('Los dos números del parley deben ser distintos'); return; }
      linea = `${pad2(campo1)}-${pad2(campo2)} ${Number(campo3)}`;
      montoLinea = Number(campo3);
    } else if (tipo === 'MONAZOS') {
      if (!campo1 || !campo2 || Number(campo2) <= 0) { setErrorEntrada('Digite número y monto'); return; }
      const esP2U2 = tipoMonazo === 'P2' || tipoMonazo === 'U2';
      if (!esP2U2 && campo1.length !== 3) { setErrorEntrada(`${tipoMonazo} requiere 3 dígitos (ej. 970)`); return; }
      if (esP2U2 && campo1.length < 2) { setErrorEntrada(`${tipoMonazo} requiere 2 o 3 dígitos`); return; }
      linea = `${campo1} ${Number(campo2)} ${tipoMonazo}`;
      montoLinea = Number(campo2);
    }

    setLineasTemp((prev) => [...prev, { linea, tipo, monto: montoLinea }]);
    limpiarCampos();
  };

  const focusNext = (next: React.RefObject<HTMLInputElement | null>) => next.current?.focus();

  const totalTemp = lineasTemp.reduce((a, l) => a + l.monto, 0);

  // ── Validar (dry-run) ──
  const validar = async () => {
    setErrorHttp(null);
    setResultado(null);
    const textoFinal = modo === 'RAPIDO'
      ? lineasTemp.map((l) => l.linea).join('\n')
      : texto;
    // En modo rápido las líneas ya van normalizadas "número monto"
    const formatoFinal = modo === 'RAPIDO' ? 'NUMERO_MONTO' : formato;
    if (!sorteoId) { setErrorHttp('Seleccione un sorteo abierto de este tipo'); return; }
    if (!localId) { setErrorHttp(esMiniBanca ? 'Seleccione un vendedor' : 'Seleccione un local/vendedor'); return; }
    if (!textoFinal.trim()) {
      setErrorHttp(modo === 'RAPIDO' ? 'Agregue al menos una línea' : 'Digite o pegue la lista');
      return;
    }
    setValidando(true);
    try {
      const r = await fetchAPI<ResultadoValidacion>('/listas-manuales/validar', {
        method: 'POST',
        body: JSON.stringify({
          local_id: localId, sorteo_id: sorteoId,
          tipo, formato: formatoFinal, texto: textoFinal,
        }),
      });
      setResultado(r);
      setExito(null);
      // Guardar EXACTAMENTE lo validado: confirmar usa esto
      setTextoValidado(textoFinal);
      setFormatoValidado(formatoFinal);
      // No se limpia nada: el usuario puede corregir y re-validar.
    } catch (e: any) {
      setErrorHttp(e.message || 'Error al validar');
    } finally {
      setValidando(false);
    }
  };

  // Cualquier edición posterior invalida la validación → hay que re-validar
  useEffect(() => {
    setResultado(null);
    setTextoValidado(null);
  }, [lineasTemp, texto, tipo, sorteoId, localId, modo]);

  // ── FASE 2: confirmar (ventas REALES) ──
  const confirmar = async () => {
    if (!textoValidado || !resultado?.valido) return;
    setConfirmando(true);
    setErrorHttp(null);
    try {
      const r = await fetchAPI<typeof exito & { ok: boolean }>('/listas-manuales/confirmar', {
        method: 'POST',
        body: JSON.stringify({
          local_id: localId, sorteo_id: sorteoId,
          tipo, formato: formatoValidado, texto: textoValidado,
        }),
      });
      setExito(r as any);
      setModalConfirmar(false);
      // No limpiamos las líneas: el usuario ve el resultado y decide.
      // Sí se exige re-validar para poder confirmar de nuevo (evita dobles).
      setResultado(null);
      setTextoValidado(null);
    } catch (e: any) {
      setModalConfirmar(false);
      setErrorHttp(`No se registró la lista: ${e.message || 'error desconocido'}`);
    } finally {
      setConfirmando(false);
    }
  };

  const totalJugadas = resultado
    ? resultado.lineas.reduce((acc, ln) => acc + (ln.valida ? ln.jugadas.length : 0), 0)
    : 0;
  const ayuda = AYUDA[tipo];
  const nombreVendedor = locales.find((l) => l.id === localId)?.nombre || localId;
  const nombreSorteo = sorteosDelTipo.find((s) => s.id === sorteoId)?.nombre || sorteoId;

  // ── Render de la zona de digitación rápida por tipo ──
  const renderRapido = () => {
    if (tipo === 'NORMAL') {
      const labels = numeroEnPrimero ? ['Número', 'Monto'] : ['Monto', 'Número'];
      const lens: [number, number] = numeroEnPrimero ? [2, 9] : [9, 2];
      return (
        <div className="flex flex-wrap items-end gap-3">
          <NumInput label={labels[0]} value={campo1} onChange={setCampo1} maxLen={lens[0]}
            onEnter={() => focusNext(ref2)} inputRef={ref1} ancho="w-28" />
          <NumInput label={labels[1]} value={campo2} onChange={setCampo2} maxLen={lens[1]}
            onEnter={agregarLinea} inputRef={ref2} ancho="w-36" />
          <Button onClick={agregarLinea}><Plus size={18} /> Agregar línea</Button>
        </div>
      );
    }
    if (tipo === 'REVENTADO') {
      return (
        <div className="flex flex-wrap items-end gap-3">
          <NumInput label="Número" value={campo1} onChange={setCampo1} maxLen={2}
            onEnter={() => focusNext(ref2)} inputRef={ref1} ancho="w-28" />
          <NumInput label="Directo" value={campo2} onChange={setCampo2} maxLen={9}
            onEnter={() => focusNext(ref3)} inputRef={ref2} ancho="w-32" />
          <NumInput label="Reventado (opcional)" value={campo3} onChange={setCampo3} maxLen={9}
            onEnter={agregarLinea} inputRef={ref3} ancho="w-36" placeholder="0" />
          <Button onClick={agregarLinea}><Plus size={18} /> Agregar línea</Button>
        </div>
      );
    }
    if (tipo === 'FECHAS') {
      return (
        <div className="flex flex-wrap items-end gap-3">
          <NumInput label="Día" value={dia} onChange={setDia} maxLen={2}
            onEnter={() => focusNext(ref1)} inputRef={refDia} ancho="w-24" />
          <NumInput label="Mes" value={mes} onChange={setMes} maxLen={2}
            onEnter={() => focusNext(ref2)} inputRef={ref1} ancho="w-24" />
          <NumInput label="Monto" value={campo2} onChange={setCampo2} maxLen={9}
            onEnter={agregarLinea} inputRef={ref2} ancho="w-36" />
          <Button onClick={agregarLinea}><Plus size={18} /> Agregar línea</Button>
        </div>
      );
    }
    if (tipo === 'PARLEY') {
      return (
        <div className="flex flex-wrap items-end gap-3">
          <NumInput label="Número 1" value={campo1} onChange={setCampo1} maxLen={2}
            onEnter={() => focusNext(ref2)} inputRef={ref1} ancho="w-28" />
          <NumInput label="Número 2" value={campo2} onChange={setCampo2} maxLen={2}
            onEnter={() => focusNext(ref3)} inputRef={ref2} ancho="w-28" />
          <NumInput label="Monto" value={campo3} onChange={setCampo3} maxLen={9}
            onEnter={agregarLinea} inputRef={ref3} ancho="w-36" />
          <Button onClick={agregarLinea}><Plus size={18} /> Agregar línea</Button>
        </div>
      );
    }
    // MONAZOS
    return (
      <div className="flex flex-wrap items-end gap-3">
        <NumInput label="Número" value={campo1} onChange={setCampo1} maxLen={3}
          onEnter={() => focusNext(ref2)} inputRef={ref1} ancho="w-28" />
        <NumInput label="Monto" value={campo2} onChange={setCampo2} maxLen={9}
          onEnter={agregarLinea} inputRef={ref2} ancho="w-36" />
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">Tipo</label>
          <select value={tipoMonazo} onChange={(e) => setTipoMonazo(e.target.value as MonazoTipo)}
            className="px-3 py-3 border-2 border-gray-200 rounded-xl font-bold">
            <option value="ORDEN">En orden</option>
            <option value="DESORDEN">Desorden</option>
            <option value="P2">Primeros 2</option>
            <option value="U2">Últimos 2</option>
          </select>
        </div>
        <Button onClick={agregarLinea}><Plus size={18} /> Agregar línea</Button>
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <ClipboardList className="text-primary-600" size={28} />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Listas Manuales</h1>
          <p className="text-sm text-gray-500">
            Validar no guarda nada. Confirmar registra ventas reales con origen LISTA MANUAL.
          </p>
        </div>
      </div>

      <Card className="p-4 space-y-4">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 text-sm text-gray-600 flex items-center gap-2">
          <Info size={16} className="shrink-0 text-gray-400" />
          Solo se permite validar listas para el día operativo actual.
          La fecha la determina el servidor y se muestra en el resultado.
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Tipo de lista</label>
            <select value={tipo} onChange={(e) => cambiarTipo(e.target.value as TipoLista)}
              className="w-full px-3 py-2 border rounded-lg">
              {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Sorteo (abierto)</label>
            <select value={sorteoId} onChange={(e) => setSorteoId(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg">
              <option value="">— Seleccionar —</option>
              {sorteosDelTipo.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
            </select>
            {!cargando && sorteosDelTipo.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">No hay sorteos abiertos de tipo {tipo}.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              {esMiniBanca ? 'Vendedor interno' : 'Local / vendedor'}
            </label>
            <select value={localId} onChange={(e) => setLocalId(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg">
              <option value="">— Seleccionar —</option>
              {locales.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
            </select>
          </div>

          {(tipo === 'NORMAL' || modo === 'PEGAR') && (
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Formato</label>
              <select value={formato} onChange={(e) => setFormato(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg">
                {FORMATOS.filter((f) => modo === 'PEGAR' || f.value !== 'AUTO')
                  .map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              {modo === 'RAPIDO' && (
                <p className="text-xs text-gray-400 mt-1">Cambia el orden de las casillas.</p>
              )}
            </div>
          )}
        </div>

        {/* Selector de modo */}
        <div className="flex gap-2">
          <button type="button" onClick={() => setModo('RAPIDO')}
            className={`px-4 py-2 rounded-lg text-sm font-medium border ${modo === 'RAPIDO'
              ? 'bg-primary-600 text-white border-primary-600'
              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            Modo rápido
          </button>
          <button type="button" onClick={() => setModo('PEGAR')}
            className={`px-4 py-2 rounded-lg text-sm font-medium border ${modo === 'PEGAR'
              ? 'bg-primary-600 text-white border-primary-600'
              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            Pegar lista
          </button>
        </div>

        {modo === 'RAPIDO' ? (
          <div className="space-y-3">
            {renderRapido()}
            {errorEntrada && (
              <div className="text-sm text-red-600 flex items-center gap-1">
                <XCircle size={15} /> {errorEntrada}
              </div>
            )}

            {/* Tabla temporal (solo frontend: no toca backend) */}
            {lineasTemp.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-gray-500">
                      <th className="py-2 px-3">#</th>
                      <th className="py-2 px-3">Línea</th>
                      <th className="py-2 px-3">Tipo</th>
                      <th className="py-2 px-3 text-right">Monto</th>
                      <th className="py-2 px-3 text-center">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineasTemp.map((l, i) => (
                      <tr key={i} className="border-t">
                        <td className="py-1.5 px-3 text-gray-400">{i + 1}</td>
                        <td className="py-1.5 px-3 font-mono">{l.linea}</td>
                        <td className="py-1.5 px-3">{l.tipo}</td>
                        <td className="py-1.5 px-3 text-right">₡{l.monto.toLocaleString('es-CR')}</td>
                        <td className="py-1.5 px-3 text-center">
                          <button type="button" title="Eliminar línea (solo de esta tabla)"
                            onClick={() => setLineasTemp((prev) => prev.filter((_, j) => j !== i))}
                            className="text-red-500 hover:text-red-700 p-1">
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center justify-between bg-gray-50 px-3 py-2 text-sm border-t">
                  <span className="text-gray-600">
                    {lineasTemp.length} línea{lineasTemp.length !== 1 ? 's' : ''} ·
                    {' '}Total temporal: <b>₡{totalTemp.toLocaleString('es-CR')}</b>
                  </span>
                  <button type="button" onClick={() => setLineasTemp([])}
                    className="text-xs text-red-600 hover:underline">
                    Limpiar todo
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm mb-2">
              <div className="flex items-center gap-2 text-blue-800 font-medium mb-1">
                <Info size={16} /> Ejemplos para {tipo}
              </div>
              <pre className="text-blue-900 text-xs whitespace-pre-wrap font-mono">{ayuda.ejemplos.join('\n')}</pre>
              <p className="text-blue-700 text-xs mt-1">{ayuda.nota}</p>
            </div>
            <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={10}
              spellCheck={false} placeholder={ayuda.ejemplos[0]}
              className="w-full px-3 py-2 border rounded-lg font-mono text-sm" />
          </div>
        )}

        {errorHttp && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm flex items-center gap-2">
            <XCircle size={18} className="shrink-0" /> {errorHttp}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={validar} disabled={validando}>
            {validando ? 'Validando…' : 'Validar lista'}
          </Button>
          {resultado?.valido && (
            <button type="button" onClick={() => setModalConfirmar(true)}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-green-600 text-white hover:bg-green-700">
              Confirmar y registrar lista
            </button>
          )}
          <span className="text-xs text-gray-400">
            {resultado?.valido
              ? 'Confirmar registrará ventas REALES.'
              : 'Valide la lista para poder confirmarla.'}
          </span>
        </div>
      </Card>

      {/* Éxito de confirmación (FASE 2) */}
      {exito && (
        <Card className="p-4 border-2 border-green-300 bg-green-50">
          <div className="flex items-center gap-2 text-green-800 font-semibold mb-2">
            <CheckCircle2 size={22} /> Lista registrada como venta real
          </div>
          <div className="text-sm text-green-900 space-y-1">
            <p>Ticket: <b className="font-mono">{exito.ticket_number || exito.ticket_id}</b></p>
            <p>{exito.lineas} línea{exito.lineas !== 1 ? 's' : ''} · {exito.jugadas} jugada{exito.jugadas !== 1 ? 's' : ''} · Total <b>₡{Number(exito.total).toLocaleString('es-CR')}</b></p>
            {exito.advertencias?.map((a, i) => (
              <p key={i} className="text-amber-700 flex items-center gap-1">
                <AlertTriangle size={14} /> {a}
              </p>
            ))}
          </div>
          <button type="button" className="mt-3 text-sm text-green-700 underline"
            onClick={() => { setExito(null); setLineasTemp([]); setTexto(''); }}>
            Empezar nueva lista
          </button>
        </Card>
      )}

      {/* Modal de confirmación (FASE 2) */}
      {modalConfirmar && resultado?.valido && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 space-y-3">
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <AlertTriangle className="text-amber-500" size={22} />
              Confirmar lista manual
            </h3>
            <p className="text-sm text-red-700 font-medium">
              Esta acción registrará ventas reales y afectará saldos. ¿Desea continuar?
            </p>
            <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
              <p><span className="text-gray-500">Vendedor:</span> <b>{nombreVendedor}</b></p>
              <p><span className="text-gray-500">Sorteo:</span> <b>{nombreSorteo}</b></p>
              <p><span className="text-gray-500">Tipo:</span> <b>{tipo}</b></p>
              <p><span className="text-gray-500">Líneas:</span> <b>{resultado.lineas_validas}</b>
                {' '}· <span className="text-gray-500">Jugadas:</span> <b>{totalJugadas}</b>
                {' '}· <span className="text-gray-500">Total:</span> <b>₡{Number(resultado.total_monto).toLocaleString('es-CR')}</b></p>
              {resultado.advertencias_globales.map((a, i) => (
                <p key={i} className="text-amber-700 text-xs flex items-center gap-1">
                  <AlertTriangle size={13} /> {a}
                </p>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" disabled={confirmando}
                onClick={() => setModalConfirmar(false)}
                className="px-4 py-2 rounded-lg text-sm border border-gray-300 text-gray-600 hover:bg-gray-50">
                Cancelar
              </button>
              <button type="button" disabled={confirmando} onClick={confirmar}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-green-600 text-white hover:bg-green-700 disabled:opacity-60">
                {confirmando ? 'Registrando…' : 'Sí, registrar ventas'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resultado del dry-run */}
      {resultado && (
        <Card className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            {resultado.valido ? (
              <span className="inline-flex items-center gap-1 text-green-700 font-semibold">
                <CheckCircle2 size={20} /> Lista VÁLIDA
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-red-700 font-semibold">
                <XCircle size={20} /> Lista INVÁLIDA
              </span>
            )}
            <Badge variant="neutral">{resultado.lineas_validas}/{resultado.total_lineas} líneas válidas</Badge>
            <Badge variant="neutral">{totalJugadas} jugadas</Badge>
            <Badge variant="neutral">Total ₡{Number(resultado.total_monto).toLocaleString('es-CR')}</Badge>
            <Badge variant="neutral">Día operativo: {resultado.fecha_operativa}</Badge>
          </div>

          {resultado.errores_globales.map((e, i) => (
            <div key={i} className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-2 text-sm flex items-center gap-2">
              <XCircle size={16} className="shrink-0" /> {e}
            </div>
          ))}
          {resultado.advertencias_globales.map((a, i) => (
            <div key={i} className="bg-amber-50 border border-amber-200 text-amber-700 rounded-lg p-2 text-sm flex items-center gap-2">
              <AlertTriangle size={16} className="shrink-0" /> {a}
            </div>
          ))}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-3">#</th>
                  <th className="py-2 pr-3">Línea original</th>
                  <th className="py-2 pr-3">Jugadas parseadas</th>
                  <th className="py-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                {resultado.lineas.map((ln) => (
                  <tr key={ln.orden} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-3 text-gray-400">{ln.orden}</td>
                    <td className="py-2 pr-3 font-mono">{ln.linea_original}</td>
                    <td className="py-2 pr-3">
                      {ln.jugadas.length === 0 && <span className="text-gray-400">—</span>}
                      {ln.jugadas.map((j, i) => (
                        <div key={i} className="font-mono text-xs text-gray-700">
                          num1={j.num1}{j.num2 ? `  num2=${j.num2}` : ''}  ₡{Number(j.monto).toLocaleString('es-CR')}
                        </div>
                      ))}
                    </td>
                    <td className="py-2">
                      {ln.valida ? <Badge variant="success">OK</Badge> : <Badge variant="danger">Error</Badge>}
                      {ln.errores.map((e, i) => (
                        <div key={i} className="text-xs text-red-600 mt-1">{e}</div>
                      ))}
                      {ln.advertencias.map((a, i) => (
                        <div key={i} className="text-xs text-amber-600 mt-1">{a}</div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
